<script setup lang="ts">
/**
 * One node and, recursively, its children.
 *
 * Every field beyond `kind`, `spanKey`, `spanPath` and `state` is optional, and unknown values must
 * render rather than throw — a portal deployed slightly behind the backend must not white-screen on a
 * span kind it has not heard of.
 *
 * Identity is `spanKey`, never `spanPath`: a path is resource-scoped, so sibling calls to one egress
 * share it.
 */
import { computed, ref, watch } from 'vue'
import { useLocalization } from '@hantera/portal-app'
import type { TraceNode } from '@/backend/traces.types'
import type { TraceGraphModel } from './trace-graph-model'

const t = useLocalization('monitoring')

const props = defineProps<{
  node: TraceNode
  depth: number

  /** Shared, so subtree sizes are read rather than recomputed per node. */
  model: TraceGraphModel

  /** Starts wide subtrees collapsed, bounding how much of a large trace mounts at once. */
  largeTrace?: boolean

  expandAll?: boolean

  /** A `spanKey`, which is unique. Selecting on `spanPath` would match every sibling sharing it. */
  selectedKey?: string

  /**
   * Ancestors of the selection, which stay open even when the collapse default would close them —
   * otherwise a deep-linked span can be selected but never rendered.
   */
  revealKeys?: Set<string>

  /** Used only to label a long gap on the edge into this node. */
  parentStartedAt?: Date
}>()

const emit = defineEmits<{
  (e: 'select', node: TraceNode): void
}>()

/** Matches the API's own cap. Beyond it the backend has already stopped descending. */
const MAX_DEPTH = 32

/** Below this depth, a wide subtree starts collapsed so the first screen stays legible. */
const COLLAPSE_DEPTH = 3

const WIDE_TRACE_SPANS = 50

const span = computed(() => props.node.span)

const children = computed(() => props.node.children ?? [])

/** Read from the shared model rather than measured here, so the tree is walked once per trace. */
const subtreeSize = computed(() => props.model.metrics.get(props.node)?.subtreeSize ?? 1)

const collapsible = computed(
  () => props.depth >= COLLAPSE_DEPTH && subtreeSize.value > WIDE_TRACE_SPANS,
)

/**
 * A large trace starts collapsed, a small one open.
 *
 * The previous default of always-open meant the collapse logic never reduced the initial mount: every
 * span was constructed regardless, which is what made a big trace stall before first paint.
 */
const expanded = ref(!props.largeTrace)

watch(
  () => props.expandAll,
  (all) => {
    expanded.value = all ?? !props.largeTrace
  },
)

/**
 * Whether this node lies on the path to the selection, and so must stay open.
 *
 * Overrides the collapse default rather than seeding `expanded`: a deep link is resolved after the
 * trace loads, which is after this node has already mounted with its default.
 */
const revealed = computed(() => props.revealKeys?.has(span.value.spanKey) === true)

watch(revealed, (reveal) => {
  if (reveal) {
    expanded.value = true
  }
})

const showChildren = computed(
  () =>
    props.depth < MAX_DEPTH &&
    children.value.length > 0 &&
    (!collapsible.value || expanded.value || revealed.value),
)

/**
 * Only a `present` or `pending` span has a row to open. Inferred and ghost nodes have nothing
 * behind them, so clicking must be disabled rather than lead to an empty panel.
 */
const clickable = computed(() => span.value.state === 'present' || span.value.state === 'pending')

const selected = computed(() => props.selectedKey === span.value.spanKey)

/**
 * Colour encodes outcome, and only for spans that have one.
 *
 * `success` is three-valued: undefined means undetermined — a pending job or an unsent sending —
 * and must render neutral. Treating it as false would paint most long traces as broken.
 */
const failed = computed(() => span.value.state === 'present' && span.value.success === false)

const nodeClass = computed(() => [
  `state-${span.value.state}`,
  { selected: selected.value, clickable: clickable.value, failed: failed.value },
])

const kindLabel = computed(() => {
  const kind = span.value.kind ?? 'span'
  return span.value.state === 'inferred' ? t('trace.node.inferredSuffix', { kind }) : kind
})

/** Explains why a node cannot be opened, rather than leaving a dead click unexplained. */
const disabledReason = computed(() => {
  switch (span.value.state) {
    case 'inferred':
      return span.value.kind === 'rule'
        ? t('trace.node.ruleNotRecorded')
        : t('trace.node.actorNotRecorded')
    case 'ghost':
      return t('trace.node.ghostUnavailable')
    default:
      return undefined
  }
})

const title = computed(
  () => span.value.label || span.value.resourceId || t('trace.node.notAvailable'),
)

/**
 * The identifying detail under the title, chosen per kind.
 *
 * A job's own label is only the last path segment, so two apps' jobs are indistinguishable without
 * the full definition id. The other kinds each have one field that answers "which one is this?".
 */
const detail = computed(() => {
  if (span.value.state === 'ghost' || span.value.state === 'inferred') return undefined

  switch (span.value.kind) {
    case 'job':
      // The full definition id, always. The title is only the last segment, so two apps' jobs of the
      // same name are otherwise indistinguishable.
      return span.value.resourceId

    case 'sending':
      // Not the category: that is already this node's title, since a sending's label *is* its
      // category. Recipient and transport are what the title does not already say.
      return [span.value.recipient, span.value.transport].filter(Boolean).join(' · ') || undefined

    case 'ingress':
    case 'egress':
      // The bare id rather than `{class}/{id}`: the title carries the method and URL, so the class
      // prefix is the one part of the path that adds nothing.
      return span.value.resourceId

    default:
      return undefined
  }
})

/** Status, outcome and duration on one line — absent parts are simply omitted. */
const subtitle = computed(() => {
  if (span.value.state === 'ghost') return t('trace.node.notAvailable')
  if (span.value.state === 'inferred') return t('trace.node.notRecorded')

  const parts: string[] = []

  if (span.value.kind === 'sending') {
    if (span.value.status) parts.push(span.value.status.toLowerCase())
    if (span.value.retryCount != null && span.value.retryCount > 0) {
      parts.push(t('trace.node.retry', { count: span.value.retryCount }))
    }
  } else if (span.value.status) {
    parts.push(span.value.status)
  }

  if (span.value.state === 'pending') {
    parts.push(
      span.value.startedAt
        ? t('trace.node.scheduledAt', { time: formatTime(span.value.startedAt) })
        : t('trace.node.scheduled'),
    )
  } else if (span.value.durationMs != null) {
    parts.push(formatDuration(span.value.durationMs))
  }

  return parts.length > 0 ? parts.join(' · ') : '—'
})

/**
 * The gap on the edge into this node, labelled only when it exceeds a minute.
 *
 * Shorter gaps are the normal case and labelling them all would bury the one that matters: a
 * months-long wait before a scheduled job ran.
 */
const gapLabel = computed(() => {
  if (!props.parentStartedAt || !span.value.startedAt) return undefined

  const gapMs = new Date(span.value.startedAt).getTime() - new Date(props.parentStartedAt).getTime()
  if (gapMs < 60_000) return undefined

  return `+${formatDuration(gapMs)}`
})

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`

  const seconds = ms / 1000
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)} s`

  const minutes = seconds / 60
  if (minutes < 60) return `${Math.round(minutes)} min`

  const hours = minutes / 60
  if (hours < 48) return `${Math.round(hours)} h`

  return `${Math.round(hours / 24)} days`
}

function formatTime(value: Date): string {
  return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function activate() {
  if (clickable.value) {
    emit('select', props.node)
  }
}
</script>

<template>
  <li class="node-row" role="treeitem" :aria-expanded="showChildren ? 'true' : undefined">
    <div class="node-line">
      <span v-if="gapLabel" class="gap">{{ gapLabel }}</span>

      <component
        :is="clickable ? 'button' : 'div'"
        class="node"
        :class="nodeClass"
        :type="clickable ? 'button' : undefined"
        :title="disabledReason ?? title"
        :aria-selected="clickable ? selected : undefined"
        @click="activate"
      >
        <span class="kind">{{ kindLabel }}</span>
        <span class="title">{{ title }}</span>
        <span v-if="detail" class="detail">{{ detail }}</span>
        <span class="subtitle">
          <!-- A marker as well as the border colour: outcome must not be conveyed by colour alone. -->
          <span v-if="failed" class="failed-marker" :title="t('trace.node.failed')">✕</span>
          {{ subtitle }}
        </span>
      </component>

      <template v-if="children.length > 0">
        <button
          v-if="collapsible"
          class="expander btn-reset"
          type="button"
          @click="expanded = !expanded"
        >
          {{ expanded ? '−' : `+${subtreeSize - 1}` }}
        </button>
        <span v-else class="arrow">→</span>
      </template>
    </div>

    <ul v-if="showChildren" class="children" role="group">
      <!-- Keyed on `spanKey`, not `spanPath`: sibling egress calls share a path, and duplicate keys
           let Vue reuse the wrong element. -->
      <TraceGraphNode
        v-for="(child, index) in children"
        :key="child.span.spanKey || `${span.spanKey}-${index}`"
        :node="child"
        :depth="depth + 1"
        :model="model"
        :large-trace="largeTrace"
        :expand-all="expandAll"
        :selected-key="selectedKey"
        :reveal-keys="revealKeys"
        :parent-started-at="span.startedAt"
        @select="emit('select', $event)"
      />
    </ul>
  </li>
</template>

<style scoped>
.node-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  list-style: none;
}

.node-line {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.node {
  display: flex;
  flex-direction: column;
  gap: 1px;
  align-items: flex-start;
  text-align: left;
  height: auto;

  min-width: 150px;
  max-width: 240px;
  padding: 5px 8px;

  background-color: hsl(var(--color-100));
  border: 1px solid hsl(var(--color-70));
  border-radius: 4px;
  font: inherit;
  color: inherit;
}

.node.clickable {
  cursor: pointer;
}

.node.clickable:hover {
  background-color: hsl(var(--color-95));
}

.node.selected {
  border-color: hsl(var(--color-30));
  box-shadow: 0 0 0 1px hsl(var(--color-30));
}

/* Only outcome-bearing nodes are coloured. Inferred and ghost nodes stay grey: colouring them
   would imply an outcome they never had. */
.node.failed {
  border-color: hsl(var(--color-hue-red) 60% 50%);
}

.node.state-inferred {
  border-style: dashed;
  color: hsl(var(--color-40));
}

.node.state-ghost {
  border-style: dotted;
  color: hsl(var(--color-50));
}

.node.state-pending {
  color: hsl(var(--color-40));
}

.kind {
  font-size: 9px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: hsl(var(--color-50));
}

.title {
  font-size: 12px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Inferred nodes have no row to open, so the label is the only detail. Allow wrapping rather
   than truncating — a truncated inferred node is illegible with no way to see the full value. */
.node.state-inferred .title {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  word-break: break-all;
}

/* The identifying line: full job definition id, egress path, sending recipient. Monospace because
   these are paths and addresses, which are read by their segments rather than as prose. */
.detail {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  font-family: monospace;
  font-size: 10px;
  color: hsl(var(--color-50));
}

.subtitle {
  font-size: 11px;
  color: hsl(var(--color-40));
}

.failed-marker {
  color: hsl(var(--color-hue-red) 60% 45%);
  font-weight: 600;
}

.arrow,
.gap {
  color: hsl(var(--color-50));
  font-size: 11px;
  white-space: nowrap;
}

.expander {
  border: 1px solid hsl(var(--color-70));
  border-radius: 3px;
  background-color: hsl(var(--color-95));
  font-size: 10px;
  padding: 1px 4px;
  cursor: pointer;
}

.children {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}
</style>
